import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';

const arrowRight = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11.2 18.1"><path d="M11.2 9l-9.1 9.1L0 16l6.9-7L0 2.1 2.1 0l9.1 9z"/></svg>`;

const gButtonCss = ".g__button{font-family:\"Roboto\", \"Noto Sans TC\", \"Noto Sans SC\", \"Noto Sans JP\", \"Noto Sans KR\", \"Prompt\", sans-serif}.g__button{font-weight:500}.g__button__host{display:inline-block;margin-bottom:1rem;box-sizing:border-box;width:100%}.g__button__host--small{margin-bottom:0.5rem}@media screen and (min-width: 768px){.g__button__host{width:auto}}.g__button{box-sizing:border-box;border-radius:0;font-size:1rem;line-height:1.15;overflow:auto;transition:background 0.2s, color 0.2s;vertical-align:middle;padding:0.875rem 2rem;text-transform:uppercase;text-decoration:none;text-align:center;display:inline-block;word-break:break-word;color:#000000;cursor:pointer;width:100%}.g__button.no-hover:hover{background:inherit;text-decoration:none}@media screen and (min-width: 768px){.g__button{width:auto}}.g__button--contained{border:1px solid #000000}.g__button--contained--medium{font-size:0.875rem;padding:0.5rem 1.5rem}.g__button--contained--small{font-size:0.75rem;padding:0.313rem 1rem}.g__button--contained--light{background:#ffffff;border:1px solid #000000}.g__button--contained--light:hover,.g__button--contained--light:focus{color:#ffffff;background:#000000;outline:none}.g__button--contained--light:active{color:#000000;background:#ffffff}.g__button--contained--dark,.g__button--contained--black{color:#ffffff;background:#000000}.g__button--contained--dark:visited,.g__button--contained--black:visited{color:#ffffff}.g__button--contained--dark:hover,.g__button--contained--dark:focus,.g__button--contained--black:hover,.g__button--contained--black:focus{color:#000000;background:#ffffff;border:1px solid #000000;outline:none}.g__button--contained--dark:active,.g__button--contained--black:active{color:#ffffff;background:#000000}.g__button--contained--dark{border:1px solid #ffffff}.g__button--contained--black:hover{border:1px solid #000000}.g__button--contained--disabled{color:#000000;border-color:#d9d9d9;background:#d9d9d9;cursor:not-allowed}.g__button--contained--disabled:visited{color:#000000}.g__button--contained--disabled:hover,.g__button--contained--disabled:active{color:#000000;background:#d9d9d9}.g__button--contained--disabled .g__icon,.g__button--contained--disabled svg{pointer-events:none}.g__button--contained--candy-blue{color:#000000;background:#6dcff6;border-color:#6dcff6}.g__button--contained--candy-blue:visited{color:#000000}.g__button--contained--candy-blue:hover,.g__button--contained--candy-blue:focus{color:#000000;background:#ffffff;outline:none}.g__button--contained--candy-blue:active{color:#000000;background:#6dcff6}.g__button--flat{background:transparent;border:0;padding:0.625rem 0;width:auto}.g__button--flat:hover,.g__button--flat:focus{background:inherit;text-decoration:underline;outline:none}.g__button--flat--primary{text-decoration:none}.g__button--flat--secondary{color:#106fad}.g__button--flat--secondary:hover,.g__button--flat--secondary:focus{text-decoration:none;color:#44abee;outline:none}.g__button--flat--medium{font-size:0.875rem}.g__button--flat--small{font-size:0.75rem}.g__button--flat--disabled{color:gray;cursor:not-allowed}.g__button--flat--disabled:visited{color:gray}.g__button--flat--disabled:hover{text-decoration:none}.g__button--flat--disabled:focus{outline:none}.g__button__chevron{margin-left:0.6em}.g__button__chevron>svg{margin-top:-0.19em;display:inline-block;vertical-align:middle;height:0.9em;width:1.4em;fill:currentcolor}.g__button--flat--dark{color:#ffffff}.g__button--flat--dark:hover,.g__button--flat--dark:focus{color:#cccccc;outline:none}a.g__button--flat--light:hover,a.g__button--flat--light:focus{color:#000000;outline:none}";

const GButton = /*@__PURE__*/ proxyCustomElement(class GButton extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.gBlur = createEvent(this, "g-blur", 7);
        this.gFocus = createEvent(this, "g-focus", 7);
        this.hasFocus = false;
        this.theme = 'light';
        this.size = 'medium';
        this.flat = false;
        this.flatTheme = 'primary';
        this.disabled = false;
        this.href = undefined;
        this.target = undefined;
        this.type = undefined;
        this.label = '';
        this.isCurrent = false;
        this.noHover = false;
        this.chevron = false;
        this.styleObject = undefined;
        this.uaEvent = undefined;
        this.customTestId = undefined;
    }
    connectedCallback() {
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleClick = this.handleClick.bind(this);
    }
    /** Sets focus on the button. */
    async setFocus() {
        this.button.focus();
    }
    /** Removes focus from the button. */
    async removeFocus() {
        this.button.blur();
    }
    handleBlur(event) {
        this.hasFocus = false;
        this.gBlur.emit(event);
    }
    handleFocus(event) {
        this.hasFocus = true;
        this.gFocus.emit(event);
    }
    handleClick(event) {
        if (this.theme === 'disabled') {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    render() {
        const isLink = Boolean(this.href) || false;
        const isButton = !isLink;
        const isDisabled = () => {
            if (this.theme === 'disabled') {
                return true;
            }
            if (this.disabled) {
                return true;
            }
            return null;
        };
        const theme = this.flat ? this.flatTheme : this.theme;
        const Tag = isButton ? 'button' : 'a';
        const baseClass = 'g__button';
        const testId = this.customTestId || baseClass;
        const flatClassName = `${baseClass}--flat`;
        const containedClassName = `${baseClass}--contained`;
        const variantClassName = () => {
            if (this.flat)
                return flatClassName;
            return containedClassName;
        };
        const componentClasses = {
            [baseClass]: true,
            [`${variantClassName()}`]: true,
            [`${variantClassName()}--${this.size}`]: Boolean(this.size),
            [`${variantClassName()}--${theme}`]: Boolean(theme),
            [`${variantClassName()}--disabled`]: isDisabled(),
            'no-hover': Boolean(this.noHover),
            'g__button--flat--dark': Boolean(this.flat && this.theme === 'dark'),
        };
        /* eslint-disable no-return-assign */
        return (h(Host, { class: `g__button__host g__button__host--${this.size}` }, h(Tag, { class: componentClasses, "data-testid": testId, "data-ua-event": this.uaEvent || null, ref: (el) => (this.button = el), disabled: isDisabled(), href: isLink ? this.href : null, target: isLink && Boolean(this.target) ? this.target : null, type: !isLink ? this.type : null, rel: isLink && Boolean(this.target) ? 'noreferrer noopener' : null, onBlur: (e) => this.handleBlur(e), onFocus: (e) => this.handleFocus(e), onClick: (e) => this.handleClick(e), "aria-label": this.label || null, "aria-current": this.isCurrent ? 'true' : null, style: this.styleObject || null }, h("slot", null), this.chevron && (h("span", { class: "g__button__chevron", "aria-hidden": "true", innerHTML: arrowRight })))));
    }
    static get style() { return gButtonCss; }
}, [4, "g-button", {
        "theme": [513],
        "size": [513],
        "flat": [4],
        "flatTheme": [1, "flat-theme"],
        "disabled": [4],
        "href": [1],
        "target": [1],
        "type": [1],
        "label": [1],
        "isCurrent": [4, "is-current"],
        "noHover": [4, "no-hover"],
        "chevron": [4],
        "styleObject": [8, "style-object"],
        "uaEvent": [1, "ua-event"],
        "customTestId": [1, "custom-test-id"],
        "hasFocus": [32],
        "setFocus": [64],
        "removeFocus": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["g-button"];
    components.forEach(tagName => { switch (tagName) {
        case "g-button":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, GButton);
            }
            break;
    } });
}

export { GButton as G, arrowRight as a, defineCustomElement as d };

//# sourceMappingURL=g-button2.js.map